import { useRef } from 'react';
import {
    BasicResponsiveImage,
    StandardTile,
    StandardTileContents,
    StandardTileImage,
    StandardTileName,
    StandardTilePrice,
} from '@vp/swan';
import { __image } from '@99designs/i18n';
import { sendNavigationEvent } from '@99designs/tracking';
import { RECOMMENDATIONS_IMAGE_ASPECT_RATIO } from '../../lib';
import { buildCloudinaryURL } from '../../lib/buildCloudinaryURL';
import { Price } from '../Price';
import type { NavigationTracking } from '@99designs/tracking';
import type { Price as PriceType } from '../../types';

interface RecommendationTileProps {
    title: string;
    imageSrc: string;
    ctaLink: string;
    fallbackImageSrc?: string;
    trackingData?: NavigationTracking;
    price?: PriceType;
    loading?: boolean;
}

export function RecommendationTile({
    title,
    imageSrc,
    ctaLink,
    fallbackImageSrc = '',
    trackingData,
    price,
}: RecommendationTileProps) {
    const errorCountRef = useRef(0);

    const fallbackImages = [
        fallbackImageSrc,
        buildCloudinaryURL(__image('PreviewImages/placeholder')),
    ];

    return (
        <StandardTile
            skin="standard"
            imageWidth="full-bleed"
            render={({ className, children }) => (
                <a
                    href={ctaLink}
                    onClick={() => {
                        if (trackingData) {
                            const { crossPlatformTracking: _, ...navigationData } = trackingData;
                            sendNavigationEvent(navigationData);
                        }
                    }}
                    data-section={trackingData?.label}
                    data-translation={trackingData?.navigationDetail}
                    data-testid="product-tile"
                    className={className}
                >
                    {children}
                </a>
            )}
        >
            <StandardTileImage>
                <BasicResponsiveImage
                    style={{ height: '100%', objectFit: 'cover' }}
                    aspectRatio={RECOMMENDATIONS_IMAGE_ASPECT_RATIO}
                    src={imageSrc}
                    alt=""
                    role="presentation"
                    onError={(e) => {
                        if (errorCountRef.current < fallbackImages.length) {
                            e.currentTarget.src = fallbackImages[errorCountRef.current];
                            errorCountRef.current += 1;
                        }
                    }}
                />
            </StandardTileImage>
            <StandardTileContents>
                <StandardTileName>{title}</StandardTileName>
                <StandardTilePrice>
                    <Price price={price} />
                </StandardTilePrice>
            </StandardTileContents>
        </StandardTile>
    );
}
